import { inject } from '@angular/core';
import { Router } from '@angular/router';
import Bugsnag from '@bugsnag/js';
import { selectAuthenticatedUser } from '@kinet-open-network/client/auth/data-access';
import { DynamicFormsService } from '@kinet-open-network/client/core/forms/util';
import { toast } from '@kinet-open-network/client/shared/ui';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter, tap } from 'rxjs/operators';
import { ErrorHandlerActions } from './error-handler.actions';
import { selectValidationErrors } from './error-handler.selectors';

export const setFormErrors$ = createEffect(
  (
    actions$ = inject(Actions),
    formsService = inject(DynamicFormsService),
    store = inject(Store),
  ) => {
    return actions$.pipe(
      ofType(ErrorHandlerActions.throwError),
      tap(({ error }) => {
        switch (error.status) {
          case 403:
            const msg = error.error?.message || error.message;
            toast({
              severity: 'danger',
              summary: '403: Chyba oprávnenia',
              detail: `Nemáte oprávnenie na vykonanie tejto akcie.\n${msg}`,
            });
            break;
          case 401:
            // toast({
            //   severity: 'danger',
            //   summary: '401: Chyba autentifikácie',
            //   detail: `Prihláste sa prosím znova.\n${error?.message}`,
            // });
            break;
          default:
            toast({
              severity: 'danger',
              summary: `${error.status}: ${error.statusText}`,
              detail: error.error?.message || error.message,
            });
            break;
        }
      }),
      filter(({ error }) => error.status === 422),
      concatLatestFrom(() => store.select(selectValidationErrors)),
      tap(([, errors]) => {
        if (errors) {
          formsService.setApiErrors(errors);

          const errCount = Object.keys(errors).length;
          let errCountText = `${errCount} chýb`;
          const errorsText = Object.entries(errors).reduce(
            (text, [key, value]) => {
              // Helper to render nested errors
              const renderValue = (val: any): string => {
                if (Array.isArray(val)) {
                  return val.map((item) => `<li>${item}</li>`).join('');
                } else if (typeof val === 'object' && val !== null) {
                  return Object.entries(val)
                    .map(([subKey, subVal]) => {
                      return `<ul><strong>${subKey}</strong>: ${renderValue(
                        subVal,
                      )}</ul>`;
                    })
                    .join('');
                } else {
                  return String(val);
                }
              };
              return (
                text +
                `<li><strong>${key}</strong>: <ul>${renderValue(
                  value,
                )}</ul></li>`
              );
            },
            '',
          );

          switch (errCount) {
            case 1:
              errCountText = `1 chyba`;
              break;
            case 2:
            case 3:
            case 4:
              errCountText = '2 chyby';
              break;
            case 5:
            default:
              `${errCount} chýb`;
              break;
          }

          toast({
            severity: 'danger',
            summary: '422: Chyba validácie',
            detail: `Skontrolujte zadané údaje (${errCountText})<ul>${errorsText}</ul>`,
          });
        }
      }),
    );
  },
  { dispatch: false, functional: true },
);

export const redirectToForbidden$ = createEffect(
  (actions$ = inject(Actions), router = inject(Router)) => {
    return actions$.pipe(
      ofType(ErrorHandlerActions.throwError),
      filter(({ error }) => error.status === 403),
      tap(() => router.navigate(['/forbidden'])),
    );
  },
  { functional: true, dispatch: false },
);

export const setBugsnagUser$ = createEffect(
  (store = inject(Store)) => {
    return store.select(selectAuthenticatedUser).pipe(
      filter((user) => !!user),
      tap((user) => {
        if (!user) {
          return;
        }

        Bugsnag.setUser(user.id, user.email, user.name);
      }),
    );
  },
  {
    dispatch: false,
    functional: true,
  },
);
